import type { TemplateMeta } from '../api/templates';
export const PRE_SIGNUP_TEMPLATE_KEY = 'dcl_pre_signup_template_v1';

export type PreSignupTemplate = {
  templateMetaId: string;
  templateId?: string;
  componentKey?: string;
  category: 'business' | 'artist';
  name?: string;
};

const STATIC_KEY_TO_CLASSIC: Record<string, string> = {
  t_static_1: '1',
  t_static_2: '2',
  t_static_3: '3',
  t_static_5: '5',
};

function persist(raw: PreSignupTemplate) {
  try {
    sessionStorage.setItem(PRE_SIGNUP_TEMPLATE_KEY, JSON.stringify(raw));
  } catch {}
  try {
    localStorage.setItem(PRE_SIGNUP_TEMPLATE_KEY, JSON.stringify(raw));
  } catch {}
}

export function savePreSignupTemplate(data: PreSignupTemplate, meta?: TemplateMeta | null) {
  const enriched: PreSignupTemplate = { ...data };
  if (meta?.componentKey) enriched.componentKey = meta.componentKey;
  if (meta?.name) enriched.name = meta.name;
  persist(enriched);
  if (meta?.id) {
    try {
      const existing = JSON.parse(localStorage.getItem('templates_cache_meta') || '{}');
      localStorage.setItem('templates_cache_meta', JSON.stringify({ ...existing, [meta.id]: meta }));
    } catch {}
  }
}

export function loadPreSignupTemplate(): PreSignupTemplate | null {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(PRE_SIGNUP_TEMPLATE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as PreSignupTemplate;
      if (parsed?.templateMetaId) return parsed;
    } catch {}
  }
  return null;
}

export function clearPreSignupTemplate() {
  try {
    sessionStorage.removeItem(PRE_SIGNUP_TEMPLATE_KEY);
  } catch {}
  try {
    localStorage.removeItem(PRE_SIGNUP_TEMPLATE_KEY);
  } catch {}
}

/** Capture ?template= from URL and persist for signup flow. */
export function captureTemplateFromUrl(): PreSignupTemplate | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = new URLSearchParams(window.location.search).get('template');
    if (!id) return null;
    let meta: TemplateMeta | null = null;
    try {
      const cached = JSON.parse(localStorage.getItem('templates_cache_meta') || '{}');
      meta = cached[id] || null;
    } catch {}
    const category =
      String(meta?.category || '').toLowerCase().includes('artist') ? 'artist' : 'business';
    const stub: PreSignupTemplate = {
      templateMetaId: id,
      category,
      componentKey: meta?.componentKey,
      name: meta?.name,
    };
    savePreSignupTemplate(stub, meta);
    return stub;
  } catch {
    return null;
  }
}

export function buildAuthSignupUrl(templateMetaId: string): string {
  return `/auth?signup=1&template=${encodeURIComponent(templateMetaId)}`;
}

/** Map gallery meta to the payload expected by handleTemplateSelect. */
export function buildTemplateSelectPayload(
  meta: TemplateMeta,
  selectedId: string
): { templateId?: string; templateMetaId?: string; componentKey?: string; variant?: string } | string {
  if (/^[1-5]$/.test(selectedId)) return selectedId;
  if (STATIC_KEY_TO_CLASSIC[selectedId]) return STATIC_KEY_TO_CLASSIC[selectedId];

  const key = String(meta?.componentKey || '').toLowerCase();
  let staticId = '1';
  if (/professional/.test(key)) staticId = '1';
  else if (/elegant/.test(key)) staticId = '2';
  else if (/modern/.test(key)) staticId = '3';
  else if (/gold/.test(key)) staticId = '4';
  else if (/classic\s*modular|modular|premium/.test(key)) staticId = '5';

  if (meta && selectedId && !/^[1-5]$/.test(selectedId)) {
    return {
      templateId: staticId,
      templateMetaId: selectedId,
      componentKey: meta.componentKey,
      variant: (meta as any)?.variant,
      templateMeta: meta,
    };
  }
  return staticId;
}

export function preSignupToSelectPayload(pre: PreSignupTemplate, meta?: TemplateMeta | null) {
  if (meta) return buildTemplateSelectPayload(meta, pre.templateMetaId);
  if (pre.templateId && /^[1-5]$/.test(pre.templateId)) return pre.templateId;
  return {
    templateId: pre.templateId || '1',
    templateMetaId: pre.templateMetaId,
    componentKey: pre.componentKey,
  };
}
